import {
  ActionIcon,
  AppShell,
  Button,
  Container,
  Group,
  Image,
  ScrollArea,
  Stack,
  Title,
} from "@mantine/core"
import { ErrorBoundary, type FallbackRender } from "@sentry/react"
import { IconChevronLeftPipe } from "@tabler/icons-react"
import { User } from "configcat-react"
import _ from "lodash"
import { useMemo, useState } from "react"
import { Link, Outlet } from "react-router-dom"

import { OnboardingResponses } from "@costory/types/endpoints/onboard"
import { OnboardingStatus } from "@costory/types/prisma-client"

import { REFRESH_INTERVAL_MS } from "@costory/shared/const"

import Gtm from "@costory/front/components/layout/Gtm"
import NativeIntercom from "@costory/front/components/layout/Intercom"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { PageContextProvider } from "@costory/front/contexts/PageContext"
import { useBrand } from "@costory/front/lib/whitelabel"
import { useAuthState } from "@costory/front/queries/auth"
import { useOnboardStatusQuery } from "@costory/front/queries/onboard"

import { AppHeader } from "./AppHeader"
import { SidebarLink } from "./SidebarLink"
import { SIDEBAR_ITEMS } from "./constants"

const AppLayoutContent = ({
  status,
}: {
  status: OnboardingResponses.Status
}) => {
  const auth = useAuthState()
  const brand = useBrand()
  const orgSlug = auth.user?.currentOrg?.slug ?? ""
  const userObject = new User(orgSlug)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const filteredSidebarItems = useMemo(() => {
    if (status.onboardingStatus === OnboardingStatus.SETUP) {
      return SIDEBAR_ITEMS.filter((item) => item.name === "Settings")
    }
    return SIDEBAR_ITEMS
  }, [status.onboardingStatus])

  const { top, bottom = [] } = useMemo(
    () => _.groupBy(filteredSidebarItems, (d) => d.position || "top"),
    [filteredSidebarItems],
  )

  return (
    <PageContextProvider>
      <AppShell
        header={{ height: 64 }}
        navbar={{ width: isCollapsed ? 70 : 220, breakpoint: "sm" }}
        footer={{ height: 0 }}
        layout="alt"
      >
        <AppHeader />
        <AppShell.Navbar
          withBorder={false}
          bg="surfaceSidebar"
          p={12}
          style={{ transition: "width 200ms" }}
        >
          <Link to="/">
            <Group p={8} wrap="nowrap">
              <Image
                w={brand.sidebarLogoWidth}
                h={brand.sidebarLogoHeight}
                src={brand.sidebarLogo}
                alt={`${brand.name} logo`}
              />
              {!isCollapsed && brand.showSidebarAppName && (
                <Title c="textSidebar" size="h2">
                  {brand.name}
                </Title>
              )}
            </Group>
          </Link>

          <Stack h="100%" justify="space-between" mt={32}>
            <ScrollArea h="85%">
              <Stack py={16} gap={8}>
                {top.map((route) => (
                  <SidebarLink
                    isParent
                    key={route.path}
                    route={route}
                    isCollapsed={isCollapsed}
                    user={userObject}
                    onboardingStatus={status}
                  />
                ))}
              </Stack>
            </ScrollArea>
            <Stack py={16} gap={8}>
              {bottom.map((route) => (
                <SidebarLink
                  key={route.path}
                  route={route}
                  isCollapsed={isCollapsed}
                  user={userObject}
                  onboardingStatus={status}
                />
              ))}
              <ActionIcon
                size="lg"
                c="textSidebarMuted"
                style={{
                  transition: "transform 200ms",
                  transform: isCollapsed ? "rotate(180deg)" : "rotate(0)",
                }}
                onClick={() =>
                  setIsCollapsed((prevCollapsed) => !prevCollapsed)
                }
              >
                <IconChevronLeftPipe />
              </ActionIcon>
            </Stack>
          </Stack>
        </AppShell.Navbar>
        <AppShell.Main>
          <Gtm />
          <NativeIntercom />
          <Container
            fluid
            mih="calc(100dvh - 64px)"
            h="calc(100dvh - 64px)"
            mah="calc(100dvh - 64px)"
            px={32}
            pt={16}
            pb={64}
            bg="surfaceGround"
            styles={{ root: { overflowY: "scroll" } }}
          >
            <ErrorBoundary
              fallback={({
                error,
                componentStack,
                resetError,
              }: Parameters<FallbackRender>[0]) => (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  <h2>Something went wrong</h2>
                  <p>
                    An error occurred in the application. Please try refreshing
                    the page.
                  </p>
                  <Button color="primary" onClick={resetError}>
                    Try again
                  </Button>
                  <details
                    style={{ whiteSpace: "pre-wrap", marginTop: "20px" }}
                  >
                    <summary>Error Details</summary>
                    {error instanceof Error ? error.toString() : String(error)}
                    <br />
                    {componentStack}
                  </details>
                </div>
              )}
            >
              <Outlet />
            </ErrorBoundary>
          </Container>
        </AppShell.Main>
      </AppShell>
    </PageContextProvider>
  )
}

export const AppLayout = () => {
  const onboardStatus = useOnboardStatusQuery(REFRESH_INTERVAL_MS)
  return (
    <QueryWrapper query={onboardStatus}>
      {({ data: status }) => <AppLayoutContent status={status} />}
    </QueryWrapper>
  )
}
